import { useCookies } from "vue3-cookies";

/**
 * Who moved a cookie.
 *
 * `internal` — this app wrote it, so the caller already knows the resulting state.
 * `external` — someone else did (AIQ on the shared domain, another tab, the browser expiring it),
 * discovered by observation rather than announcement.
 *
 * Consumers that need to resync on a change care about exactly this distinction. Deriving it from
 * provenance rather than from event ordering is what keeps the reconciliation race-free: an
 * announced write is internal no matter when the observer happens to run.
 */
export type CookieChangeOrigin = "internal" | "external";

export type CookieChangeHandler = (value: string | undefined, origin: CookieChangeOrigin) => void;

const POLL_INTERVAL_MS = 1000;

interface CookieStore extends EventTarget {
  addEventListener(type: "change", listener: () => void): void;
  removeEventListener(type: "change", listener: () => void): void;
}

type Watch = {
  value: string | undefined;
  handlers: Set<CookieChangeHandler>;
  stop: () => void;
};

const watches = new Map<string, Watch>();

const { cookies } = useCookies();

export const readCookie = (name: string): string | undefined => cookies.get(name) || undefined;

const emit = (name: string, origin: CookieChangeOrigin): void => {
  const watch = watches.get(name);
  if (!watch) return;

  const next = readCookie(name);
  if (next === watch.value) return;

  watch.value = next;
  for (const handler of [...watch.handlers]) handler(next, origin);
};

/**
 * Observation only ever reports `external`: anything this app wrote announces itself through
 * `writeCookie`/`clearCookie` before the observer can notice it.
 */
const startObserving = (name: string): (() => void) => {
  const sync = (): void => emit(name, "external");

  const cookieStore = (window as Window & { cookieStore?: CookieStore }).cookieStore;
  const pollId = cookieStore ? undefined : setInterval(sync, POLL_INTERVAL_MS);

  cookieStore?.addEventListener("change", sync);
  window.addEventListener("focus", sync);
  document.addEventListener("visibilitychange", sync);

  return () => {
    if (pollId) clearInterval(pollId);
    cookieStore?.removeEventListener("change", sync);
    window.removeEventListener("focus", sync);
    document.removeEventListener("visibilitychange", sync);
  };
};

export const subscribeToCookie = (name: string, handler: CookieChangeHandler): (() => void) => {
  let watch = watches.get(name);

  if (!watch) {
    watch = { value: readCookie(name), handlers: new Set(), stop: () => {} };
    watches.set(name, watch);
    watch.stop = startObserving(name);
  }

  watch.handlers.add(handler);

  return () => {
    const active = watches.get(name);
    if (!active) return;

    active.handlers.delete(handler);

    if (active.handlers.size === 0) {
      active.stop();
      watches.delete(name);
    }
  };
};

/** Announce a write this app just made, so subscribers see it as `internal`. */
const announce = (name: string): void => emit(name, "internal");

export const clearCookie = (name: string, path: string, domain: string): void => {
  cookies.remove(name, path, domain);
  announce(name);
};
